import React, { useState, useEffect } from 'react';
import { FileExplorer } from './apps/FileExplorer';
import { Notepad } from './apps/Notepad';
import { ImageViewer } from './apps/ImageViewer';
import { DisplayProperties } from './apps/DisplayProperties';
import { ControlPanel } from './apps/ControlPanel';
import { AppStore } from './apps/AppStore';
import { MusicPlayer } from './apps/MusicPlayer';
import { Terminal } from './apps/Terminal';
import { SystemProperties } from './apps/SystemProperties';
import { ComingSoon } from './apps/ComingSoon';
import { OSGames } from './apps/OSGames';
import { SoundSettings } from './apps/SoundSettings';
import { MouseSettings } from './apps/MouseSettings';
import { KeyboardSettings } from './apps/KeyboardSettings';
import { NetworkSettings } from './apps/NetworkSettings';
import { DateTimeSettings } from './apps/DateTimeSettings';
import { AppearanceSettings } from './apps/AppearanceSettings';
import { SecuritySettings } from './apps/SecuritySettings';
import { UserSettings } from './apps/UserSettings';
import { GameControllerSettings } from './apps/GameControllerSettings';
import { PrinterSettings } from './apps/PrinterSettings';
import { DiskDefrag } from './apps/DiskDefrag';
import { OfficeSuite } from './apps/OfficeSuite';
import { MapleWord } from './apps/MapleWord';
import { MapleSheet } from './apps/MapleSheet';
import { MapleCalc } from './apps/MapleCalc';
import { MapleMoney } from './apps/MapleMoney';
import { MapleStats } from './apps/MapleStats';
import { MapleSlide } from './apps/MapleSlide';
import { PaintProgram } from './apps/PaintProgram';
import { SupabaseFileExplorer } from './apps/SupabaseFileExplorer';
import { WebBrowser } from './apps/WebBrowser';
import { DOSEmulator } from './apps/DOSEmulator';
import { UniversalEmulator } from './apps/UniversalEmulator';
import { HexEditor } from './apps/HexEditor';
import { ContextMenuDemo } from './apps/ContextMenuDemo';
import CodeEditor from './apps/CodeEditor';
import EditorSettings from './apps/EditorSettings';
import { FileSystemNavigator } from '../utils/filesystem';
import ModuleFrame from './apps/ModuleFrame';
import { BugReport } from './apps/BugReport';
import { generateFindingsFromCsv } from '@/services/ModelService';
import { AICenter } from './apps/AICenter';
import AITaskManager from './apps/AITaskManager';
import AICredits from './apps/AICredits';
import ProtectionHistory from './apps/ProtectionHistory';
import RecoverableAppErrorBoundary from './RecoverableAppErrorBoundary';
import { clearPersistedAiChatTabs } from '@/OS/lib/appRecovery';

import AIIsolation from './apps/AIIsolation';
import EncryptionSettings from './apps/EncryptionSettings';
import BlueprintStudio from './apps/BlueprintStudio';
import Search from './apps/Search';
import Run from './apps/Run';
import { SystemInformation } from './apps/SystemInformation';
import OSHelp from './apps/OSHelp';
import MapleEngineApp from './apps/MapleEngineApp';
import Cortex from './apps/Cortex';
import MapleGIS from './apps/MapleGIS';
import MapleGofer from './apps/MapleGofer';
import MapleClaw from './apps/MapleClaw';
import ModuleHost from './apps/ModuleHost';
import WebAppRunner from './apps/WebAppRunner';
import MapleStar from './apps/MapleStar';
import ScreenshotTool from './apps/ScreenshotTool';
import FreeMapleAI from './apps/FreeMapleAI';
import { MapleAccess } from './apps/MapleAccess';
import AIChat from './apps/AIChat';
import FlightSim from './apps/FlightSim';
import GrandTheftBoxy from './apps/GrandTheftBoxy';
import Messenger from './apps/Messenger';
import MapleSeedVault from './apps/MapleSeedVault';
import TaskManager from './apps/TaskManager';
import MapleOSUpdates from './apps/MapleOSUpdates';
import ProfileApp from './apps/ProfileApp';
import PacManRunner from './apps/PacManRunner';

import AIProjects from './apps/AIProjects';
import AIKnowledge from './apps/AIKnowledge';

import AIBlueprints from './apps/AIBlueprints';

interface WindowContentProps {
  appId: string;
  windowId: string;
  filePath?: string;
  fileContent?: string;
  onOpenWindow?: (appId: string, title: string, filePath?: string, content?: string) => void;
  onBackgroundChange?: (background: string) => void;
  onClose?: () => void;
  onShowSplash?: (show: boolean) => void;
  route?: string;
}

export const globalNavigator = new FileSystemNavigator();

let refreshCallbacks: (() => void)[] = [];

export const addRefreshCallback = (callback: () => void) => {
  refreshCallbacks.push(callback);
};

export const removeRefreshCallback = (callback: () => void) => {
  refreshCallbacks = refreshCallbacks.filter(cb => cb !== callback);
};

const notifyRefresh = () => {
  refreshCallbacks.forEach(callback => callback());
};

export const WindowContent: React.FC<WindowContentProps> = ({ 
  appId, 
  windowId, 
  filePath, 
  fileContent,
  onOpenWindow,
  onBackgroundChange,
  onClose,
  onShowSplash,
  route
}) => {
  const appDisplayName = appId
    .split('-')
    .map((part) => (part ? part.charAt(0).toUpperCase() + part.slice(1) : part))
    .join(' ');

  const recoverCurrentAppState = () => {
    if (appId === 'ai-chat') {
      clearPersistedAiChatTabs();
    }

    globalThis.window.dispatchEvent(
      new CustomEvent('mapleos:recover-app-state', {
        detail: { appId },
      })
    );
    onClose?.();
  };

  const handleOpenFile = (path: string, content: string) => {
    if (onOpenWindow) {
      const fileName = path.split('\\').pop() || 'Untitled';
      const extension = fileName.split('.').pop()?.toLowerCase();

      if (extension === 'mword') {
        onOpenWindow('maple-word', `MapleWord - ${fileName}`, path, content);
      } else if (extension === 'html') {
        onOpenWindow('maple-word', `MapleWord - ${fileName}`, path, content);
      } else if (extension === 'mslide' || extension === 'mapleslide') {
        onOpenWindow('maple-slide', `MapleSlide - ${fileName}`, path, content);
      } else if (extension === 'msheet' || extension === 'sheet') {
        onOpenWindow('maple-sheet', `MapleSheet - ${fileName}`, path, content);
      } else if (extension === 'mmoney' || extension === 'mmny') {
        onOpenWindow('maple-money', `MapleMoney - ${fileName}`, path, content);
      } else if (extension === 'mstats') {
        onOpenWindow('maple-stats', `MapleStats - ${fileName}`, path, content);
      } else if (extension === 'mseed') {
        onOpenWindow('maple-seed-vault', `MapleSeed Vault - ${fileName}`, path, content);
      } else if (['jpg', 'jpeg', 'png', 'gif', 'webp'].includes(extension || '')) {
        onOpenWindow('image-viewer', `Image Viewer - ${fileName}`, path, content);

      } else if (extension === 'wav') {
        onOpenWindow('music-player', `Music Player - ${fileName}`, path, content);
      } else if (extension === 'bat' || extension === 'cmd' || extension === 'sh') {
        onOpenWindow('terminal', `Terminal - ${fileName}`, path, content);
      } else {
        const textLike = ['txt', 'md', 'csv', 'json', 'js', 'ts', 'tsx', 'css', 'ini', 'log', 'conf', 'sys'];
        if (!extension || textLike.includes(extension)) {
          onOpenWindow('notepad', `Notepad - ${fileName}`, path, content);
        } else {
          onOpenWindow('notepad', `Notepad - ${fileName}`, path, content);
        }
      }
    }
  };

  const handleSupabaseFileOpen = (file: any, appType: string) => {
    if (onOpenWindow) {
      if (appType === 'MapleWord') {
        onOpenWindow('maple-word', `MapleWord - ${file.file_name}`, file.file_path, file.content);
      } else if (appType === 'MapleSlide') {
        onOpenWindow('maple-slide', `MapleSlide - ${file.file_name}`, file.file_path, file.content);
      } else if (appType === 'Notepad') {
        onOpenWindow('notepad', `Notepad - ${file.file_name}`, file.file_path, file.content);
      } else if (appType === 'Image Viewer') {
        onOpenWindow('image-viewer', `Image Viewer - ${file.file_name}`, file.file_path, file.content);
      }
    }
  };

  const handleSaveFile = async (content: string, path: string) => {
    try {
      const updateResult = await globalNavigator.updateFileContent(path, content);
      const createResult = updateResult || await globalNavigator.createFile(path, content);
      
      if (updateResult || createResult) {
        notifyRefresh();
      }
    } catch (error) {
      console.error('Error saving file:', error);
    }
  };

  useEffect(() => {
    const handler = async (ev: Event) => {
      const custom = ev as CustomEvent<{ csvText: string; date?: string }>;
      const { csvText, date } = custom.detail || { csvText: '', date: undefined };
      if (!csvText) return;

      const dateStr = date || new Date().toISOString().slice(0, 10);
      const desktop = 'C:\\User\\Vince\\Desktop';
      const briefBase = `${desktop}\\Briefings\\${dateStr}`;

      const { findingsMd, deckOutline } = await generateFindingsFromCsv(csvText, dateStr);
      await globalNavigator.createFolder(`${desktop}\\Briefings`);
      await globalNavigator.createFolder(briefBase);
      await globalNavigator.createFolder(`${briefBase}\\Assets`);
      await globalNavigator.createFile(`${briefBase}\\Findings.md`, findingsMd);
      await globalNavigator.createFile(`${briefBase}\\Deck.mapleslide`, deckOutline);
      await globalNavigator.createFile(`${briefBase}\\Assets\\dataset.csv`, csvText);

      if (onOpenWindow) {
        onOpenWindow('maple-slide', `MapleSlide - Briefing ${dateStr}`, `${briefBase}\\Deck.mapleslide`, deckOutline);
        onOpenWindow('notepad', `Notepad - Findings.md`, `${briefBase}\\Findings.md`, findingsMd);
        onOpenWindow('file-explorer', `Briefing Assets`, `${briefBase}\\Assets`);
      }
    };
    window.addEventListener('mapleos-csv-to-findings', handler as EventListener);
    return () => window.removeEventListener('mapleos-csv-to-findings', handler as EventListener);
  }, [onOpenWindow]);

  const renderApp = () => {
    switch (appId) {
      case 'maple-gofer':
        return <MapleGofer />;

      case 'mapleclaw':
        return <MapleClaw />;

      case 'demythos':
        return <Terminal onOpenWindow={onOpenWindow} navigator={globalNavigator} filePath="__demythos__" fileContent={fileContent} />;
      
      case 'file-explorer':
        return <FileExplorer onOpenFile={handleOpenFile} onOpenWindow={onOpenWindow} navigator={globalNavigator} initialPath={filePath} />;

      case 'supabase-file-explorer':
        return <SupabaseFileExplorer onOpenFile={handleSupabaseFileOpen} />;
      
      case 'notepad':
        return (
          <Notepad 
            filePath={filePath}
            initialContent={fileContent}
            onSave={handleSaveFile}
            onOpenWindow={onOpenWindow}
          />
        );
      
      case 'image-viewer':
        return (
          <ImageViewer 
            imagePath={filePath}
            imageUrl={fileContent}
            navigator={globalNavigator}
            onOpenFile={handleOpenFile}
          />
        );
      
      case 'control-panel':
        return <ControlPanel onOpenWindow={onOpenWindow} onClose={onClose} onShowSplash={onShowSplash} />;
      
      case 'app-store':
        return <AppStore />;
      
      case 'display-properties':
        return <DisplayProperties onBackgroundChange={onBackgroundChange} onClose={onClose} />;
      
      case 'disk-defrag':
        return <DiskDefrag />;
      
      case 'office-suite':
        return <OfficeSuite onOpenApp={onOpenWindow} />;
      
      case 'maple-word': {
        let chatId: string | undefined;
        let projectId: string | undefined;
        try {
          if (route) {
            const parsed = JSON.parse(route);
            if (parsed && typeof parsed === 'object') {
              chatId = parsed.chatId;
              projectId = parsed.projectId;
            }
          }
        } catch {}
        return <MapleWord filePath={filePath} initialContent={fileContent} onOpenWindow={onOpenWindow} chatId={chatId} projectId={projectId} />;
      }
      
      case 'maple-sheet':
        return <MapleSheet filePath={filePath} initialContent={fileContent} />;
      
      case 'maple-calc':
        return <MapleCalc />;
      case 'maple-money':
        return <MapleMoney filePath={filePath} initialContent={fileContent} />;
      
      case 'maple-stats':
        return <MapleStats filePath={filePath} initialContent={fileContent} onClose={onClose} />;

      case 'maple-slide':
        return <MapleSlide filePath={filePath} initialContent={fileContent} />;

      case 'paint-program':
        return <PaintProgram />;
      
      case 'web-browser':
        return <WebBrowser windowId={windowId} onClose={onClose} />;
      
      case 'canxp-module':
        return <ModuleFrame path={filePath} />;
      
      case 'ai-chat':
        return <AIChat onOpenWindow={onOpenWindow} initialChatId={filePath} />;
      
      case 'ai-projects':
        return <AIProjects onOpenWindow={onOpenWindow} initialProjectId={filePath} />;

      case 'ai-knowledge':
        return <AIKnowledge initialKnowledgeBaseId={filePath} />;
      
      case 'ai-blueprints':
        return <AIBlueprints onOpenWindow={onOpenWindow} initialBlueprintId={filePath} />;

      case 'ai-center':
        return <AICenter onOpenWindow={onOpenWindow} />;

      case 'ai-task-manager':
        return <AITaskManager />;

      case 'ai-credits':
        return <AICredits />;
      
      case 'ai-isolation':
        return <AIIsolation />;

      case 'sound-settings':
        return <SoundSettings />;
      
      case 'mouse-settings':
        return <MouseSettings />;
      
      case 'keyboard-settings':
        return <KeyboardSettings />;
      
      case 'network-settings':
        return <NetworkSettings />;
      
      case 'datetime-settings':
        return <DateTimeSettings />;
      
      case 'appearance-settings':
        return <AppearanceSettings />;
      
      case 'security-settings':
        return <SecuritySettings />;
      
      case 'user-settings':
        return <UserSettings onOpenWindow={onOpenWindow} />;
      
      case 'profile':
        return <ProfileApp />;
      
      case 'game-controller-settings':
        return <GameControllerSettings />;
      
      case 'printers':
        return <PrinterSettings />;
      
      case 'music-player':
        return <MusicPlayer navigator={globalNavigator} initialTrackPath={filePath} />;
      
      case 'terminal':
        return <Terminal navigator={globalNavigator} onFileSystemChange={notifyRefresh} onOpenWindow={onOpenWindow} filePath={filePath} fileContent={fileContent} />;
      
      case 'system-properties':
        return <SystemProperties />;

      case 'mapleos-updates':
        return <MapleOSUpdates />;
      
      case 'os-games':
        return <OSGames onClose={onClose} onOpenWindow={onOpenWindow} />;

      case 'DOSEmulator':
        return <DOSEmulator onClose={onClose} />;
      
      case 'universal-emulator':
        return <UniversalEmulator onClose={onClose} />;
      
      case 'hex-editor':
        return (
          <HexEditor 
            filePath={filePath}
            initialData={fileContent}
            onClose={onClose}
          />
        );
      
      case 'maple-seed-vault':
        return <MapleSeedVault filePath={filePath} fileContent={fileContent} />;
      
      case 'code-editor':
        return <CodeEditor onOpenWindow={onOpenWindow} />;
      
      case 'editor-settings':
        return <EditorSettings />;
      
      case 'context-demo':
        return <ContextMenuDemo />;
      
      case 'search':
        return <Search onOpenWindow={onOpenWindow} navigator={globalNavigator} />;
      
      case 'run':
        return <Run onOpenWindow={onOpenWindow} />;
      
      case 'help':
        return <OSHelp />;
      
      case 'recycle-bin':
        return <FileExplorer onOpenFile={handleOpenFile} onOpenWindow={onOpenWindow} navigator={globalNavigator} initialPath={'C:\\$Recycle.Bin'} />;
      
      case 'network':
        return <ComingSoon appName="Network" />;

      case 'messenger':
        return <Messenger onOpenWindow={onOpenWindow} />;

      case 'bug-report':
        return <BugReport />;
      
      case 'protection-history':
        return <ProtectionHistory />;
      
      case 'encryption-settings':
        return <EncryptionSettings />;
      
      case 'blueprint-studio':
        return <BlueprintStudio />;
      
      case 'task-manager':
        return <TaskManager />;
      
      case 'system-information':
        return <SystemInformation />;
      
      case 'maple-engine':
        return <MapleEngineApp />;

      case 'cortex':
        return <Cortex />;

      case 'flight-sim':
        return <FlightSim />;

      case 'grand-theft-boxy':
        return <GrandTheftBoxy />;
      
      case 'maple-gis':
        return <MapleGIS />;

      case 'pacman-runner':
        return <PacManRunner />;

      case 'module-host':
        return <ModuleHost initialProjectPath={filePath} onOpenWindow={onOpenWindow} />;

      case 'web-app-runner':
        return <WebAppRunner rootPath={filePath} onOpenWindow={onOpenWindow} />;

      case 'maple-star':
        return <MapleStar />;
      
      case 'screenshot-tool':
        return <ScreenshotTool onOpenWindow={onOpenWindow} />;
      
      case 'maple-access':
        console.log('[WindowContent] Rendering MapleAccess', { windowId, appId, filePath });
        return <MapleAccess onOpenWindow={onOpenWindow} filePath={filePath} />;
      
      case 'free-maple-ai':
        return <FreeMapleAI />;
      
      default:
        return <ComingSoon appName="Unknown Application" />;
    }
  };

  return (
    <div
      className="h-full w-full flex flex-col"
      style={{
        position: 'relative',
        overflow: 'hidden',
        transform: 'translateZ(0)',
        willChange: 'transform'
      }}
    >
      <RecoverableAppErrorBoundary
        appId={appId}
        appName={appDisplayName}
        windowId={windowId}
        onRecover={recoverCurrentAppState}
      >
        {renderApp()}
      </RecoverableAppErrorBoundary>
    </div>
  );
};